import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../index';

export type PlayerAchievements = {
  verticals?: any[];
  achievements?: any[];
  flattenedAchievements?: any[];
  flattenedAchievementsByCategory?: any[];
  statusFilters?: any[];
};

interface PlayerAchievementsState {
  data: PlayerAchievements | null;
  loading: boolean;
  error: string | null;
  lastFetched: string | null;
}

const initialState: PlayerAchievementsState = {
  data: null,
  loading: false,
  error: null,
  lastFetched: null
};

const apiUrl = window.config.playerApiUrl;

export const fetchPlayerAchievements = createAsyncThunk(
  'playerAchievements/fetchPlayerAchievements',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      if (state.authentication.access_token && state.authentication.auth_type === 'user') {
        const response = await axios.get<PlayerAchievements>(`${apiUrl}/player/data/player-achievements`, {
          headers: {
            Authorization: 'Bearer ' + state.authentication.access_token,
          },
        });

        if (response.data) {
          return { data: response.data, success: true };
        }
      }
      return rejectWithValue({
        error: 'Failed to fetch player achievements',
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  }
);

const playerAchievementsSlice = createSlice({
  name: 'playerAchievements',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayerAchievements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlayerAchievements.fulfilled, (state, action: PayloadAction<{ data: PlayerAchievements; success: boolean; }>) => {
        state.loading = false;
        state.data = action.payload.data;

        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchPlayerAchievements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Unknown error occurred';
      });
  }
});


// Export selectors
export const selectPlayerAchievements = (state: { playerAchievements: PlayerAchievementsState; }) => state.playerAchievements.data;
export const selectPlayerAchievementsLoading = (state: { playerAchievements: PlayerAchievementsState; }) => state.playerAchievements.loading;
export const selectPlayerAchievementsError = (state: { playerAchievements: PlayerAchievementsState; }) => state.playerAchievements.error;
export const selectLastFetched = (state: { playerAchievements: PlayerAchievementsState; }) => state.playerAchievements.lastFetched;

export default playerAchievementsSlice.reducer;
